import { useEffect, useRef, useState, useCallback, useImperativeHandle, forwardRef } from "react";
import { Eraser } from "lucide-react";

/**
 * Canvas-based signature pad — uses POINTER EVENTS for a unified mouse/touch/
 * stylus implementation. Pointer events are required (not synthetic React
 * onTouch handlers) because React >=17 registers touch listeners as
 * `passive: true` and silently drops our `preventDefault()` call — which on
 * mobile causes the page to SCROLL instead of recording the signature. With
 * pointer events + `touch-action: none`, the browser never starts scrolling
 * and every drag is captured cleanly.
 *
 * Imperative API: { clear(), toPNG(), isEmpty() }
 */
const SignaturePad = forwardRef(function SignaturePad({ height = 180, label, testid = "sigpad", onChange }, ref) {
  const canvasRef = useRef(null);
  const ctxRef = useRef(null);
  const isDrawing = useRef(false);
  const last = useRef(null);
  const [hasContent, setHasContent] = useState(false);

  // Crisp lines on retina + responsive width via ResizeObserver.
  useEffect(() => {
    const cvs = canvasRef.current;
    if (!cvs) return;
    const setSize = () => {
      const dpr = Math.max(1, Math.min(window.devicePixelRatio || 1, 3));
      const rect = cvs.parentElement.getBoundingClientRect();
      // Save existing content so we can re-paint after resize.
      const oldDataUrl = hasContent ? safeToDataURL(cvs) : null;
      cvs.width = Math.floor(rect.width * dpr);
      cvs.height = Math.floor(height * dpr);
      cvs.style.width = `${rect.width}px`;
      cvs.style.height = `${height}px`;
      const ctx = cvs.getContext("2d");
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.scale(dpr, dpr);
      ctx.lineWidth = 2.2;
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      ctx.strokeStyle = "#0f172a";
      ctxRef.current = ctx;
      // Repaint previous content scaled to new size.
      if (oldDataUrl) {
        const img = new Image();
        img.onload = () => ctx.drawImage(img, 0, 0, rect.width, height);
        img.src = oldDataUrl;
      }
    };
    setSize();
    const ro = new ResizeObserver(setSize);
    ro.observe(cvs.parentElement);
    return () => ro.disconnect();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [height]);

  const pointFrom = (ev) => {
    const rect = canvasRef.current.getBoundingClientRect();
    return { x: ev.clientX - rect.left, y: ev.clientY - rect.top };
  };

  // Pointer Events — work for mouse, touch and stylus uniformly. No React
  // passive-listener pitfalls because the browser uses pointercancel naturally.
  const onPointerDown = useCallback((ev) => {
    ev.preventDefault();
    try { canvasRef.current.setPointerCapture(ev.pointerId); } catch { /* noop */ }
    isDrawing.current = true;
    last.current = pointFrom(ev);
    // Mark non-empty right on the first contact so the placeholder hides
    // and the line shows even before the first move.
    if (!hasContent) setHasContent(true);
    // Dot for taps without movement.
    const ctx = ctxRef.current;
    ctx.beginPath();
    ctx.arc(last.current.x, last.current.y, 1.1, 0, Math.PI * 2);
    ctx.fillStyle = "#0f172a";
    ctx.fill();
  }, [hasContent]);

  const onPointerMove = useCallback((ev) => {
    if (!isDrawing.current) return;
    ev.preventDefault();
    const p = pointFrom(ev);
    const ctx = ctxRef.current;
    ctx.beginPath();
    ctx.moveTo(last.current.x, last.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    last.current = p;
  }, []);

  const onPointerUp = useCallback((ev) => {
    if (!isDrawing.current) return;
    isDrawing.current = false;
    last.current = null;
    try { canvasRef.current.releasePointerCapture(ev.pointerId); } catch { /* noop */ }
    if (onChange) {
      try { onChange(canvasRef.current.toDataURL("image/png")); } catch { /* noop */ }
    }
  }, [onChange]);

  const clear = useCallback(() => {
    const cvs = canvasRef.current;
    const ctx = ctxRef.current;
    if (!cvs || !ctx) return;
    ctx.save();
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, cvs.width, cvs.height);
    ctx.restore();
    setHasContent(false);
    if (onChange) onChange("");
  }, [onChange]);

  useImperativeHandle(ref, () => ({
    clear,
    isEmpty: () => !hasContent,
    toPNG: () => (hasContent ? canvasRef.current.toDataURL("image/png") : ""),
  }), [clear, hasContent]);

  return (
    <div className="w-full" data-testid={`${testid}-wrapper`}>
      {label && (
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs uppercase tracking-widest font-semibold text-slate-500">{label}</span>
          <button
            type="button"
            onClick={clear}
            data-testid={`${testid}-clear`}
            className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-rose-600"
          >
            <Eraser className="h-3.5 w-3.5" /> Leeren
          </button>
        </div>
      )}
      <div className="relative rounded-xl border border-dashed border-slate-300 bg-slate-50 overflow-hidden">
        <canvas
          ref={canvasRef}
          data-testid={testid}
          // touch-action: none → browser never hijacks the gesture for scroll/zoom.
          // overscroll-behavior: contain → no rubber-band on iOS when we miss the canvas.
          style={{ touchAction: "none", overscrollBehavior: "contain", userSelect: "none", WebkitUserSelect: "none" }}
          className="block w-full cursor-crosshair"
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
          onPointerLeave={onPointerUp}
        />
        {!hasContent && (
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center text-slate-400 text-sm">
            Hier mit Maus, Finger oder Stylus unterschreiben
          </div>
        )}
      </div>
    </div>
  );
});

function safeToDataURL(canvas) {
  try { return canvas.toDataURL("image/png"); } catch { return null; }
}

export default SignaturePad;
